import React, { useId } from 'react'
import { Flex, Input, Select } from 'antd'
import type { ValueInputProps } from '@pushly/aqe'
import { emitTermSet, readTermSet, rendersTermSet } from './term-set-value'

/** The value a custom condition holds: a single typed term, or a set of them. */
type CustomPropertyValue = string | number | string[] | null

/**
 * The value control for a custom profile property — the registry's free-input escape
 * hatch and any custom field whose OpenSearch mapping the catalog could not resolve.
 *
 * Which property is tested is chosen by the row's field selector, not here: the field
 * key travels to the wire as the condition's property verbatim, so this control neither
 * names the property nor prefixes it, and legacy's `profile.` re-prefixing has no place
 * in it. All this emits is the value the chosen operator compares against.
 *
 * The arity is the operator's own, asked through the same shape logic the builder answers
 * it with everywhere else (`./term-set-value`): a set operator is edited as free-typed
 * tags, and a single-value operator as one text box. Reading it off the stored value
 * instead is what showed the same field and operator as a set after an operator switch —
 * which seeds an empty list — and as one text box when a stored scalar was reloaded,
 * where a set operator holding a scalar is an ordinary stored document.
 *
 * No `meta.custom` is written. Legacy stamped it to recover the builder property on
 * reload; the new builder recovers the field from the property key, so the marker is
 * inert. A stored one is left untouched — this control authors no meta at all — so a
 * segment that carries it round-trips unchanged.
 *
 * Existence (`exists` / `not_exists`) does not reach this renderer: the editor reads no
 * value from those operators and shows no value row, while the field selector above still
 * names the property the condition tests.
 */
export function CustomPropertyInput({
    value,
    onChange,
    field,
    operator,
    disabled,
    error,
    size,
    getPopupContainer,
}: ValueInputProps<CustomPropertyValue>) {
    const valueId = `${useId()}-value`

    // The editor shows no label of its own for a field with its own renderer, so the box names
    // itself. A custom property's box is the one that most needs it: the row above names the
    // property being tested, and without this the term being compared against is an unlabelled box.
    const label = <label htmlFor={valueId}>Value</label>

    if (rendersTermSet(operator, field, value)) {
        return (
            <Flex className="segment-custom-property" vertical gap={4}>
                {label}
                <Select
                    id={valueId}
                    className="segment-custom-property__terms"
                    style={{ width: '100%' }}
                    mode="tags"
                    tokenSeparators={[',']}
                    placeholder="Provide one or more values"
                    value={readTermSet(value)}
                    onChange={(next: string[]) => onChange(emitTermSet(operator, next))}
                    disabled={disabled}
                    size={size}
                    status={error ? 'error' : undefined}
                    getPopupContainer={getPopupContainer}
                />
            </Flex>
        )
    }

    return (
        <Flex className="segment-custom-property" vertical gap={4}>
            {label}
            <Input
                id={valueId}
                className="segment-custom-property__value"
                placeholder="Provide a value"
                value={value == null ? '' : String(value)}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                size={size}
                status={error ? 'error' : undefined}
            />
        </Flex>
    )
}
